import { gql } from '../../../core/graphql-client.js';
import { router } from '../../../core/router.js';
import { auth } from '../../../core/auth-store.js';
import { toast } from '../../../core/event-bus.js';
import type { Person } from '../../../types.js';

export class PeopleListPage extends HTMLElement {
  private people: Person[] = [];
  private search = '';

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie osób..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const data = await gql.query<{ people: Person[] }>(`
        query { people { id firstName lastName displayName email phone isActive isAdmin roles { slug displayName } } }
      `);
      this.people = data.people;
      this.render();
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private render(): void {
    this.innerHTML = `
      <div class="page">
        <div class="page-header">
          <h1>Osoby</h1>
        </div>
        <input class="search-input" type="text" placeholder="Szukaj..." id="search" value="${this.search}">
        <div class="people-list" id="people-list"></div>
        <button class="fab" id="add-btn">+</button>
      </div>
      <style>
        .people-list { margin-top: var(--space-md); }
        .list-item-content { flex: 1; min-width: 0; }
        .list-item-title { font-weight: 500; }
        .list-item-subtitle {
          font-size: .8rem;
          color: var(--color-on-surface-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .list-item-contact {
          font-size: .75rem;
          color: var(--color-on-surface-muted);
          opacity: .7;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media (min-width: 800px) {
          .list-item-content {
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            column-gap: var(--space-md);
          }
          .list-item-title { flex: none; }
          .list-item-contact { flex: none; font-size: .8rem; }
          .list-item-subtitle { flex-basis: 100%; }
        }
        .btn-sm { padding: 4px 8px; font-size: .8rem; }
        .impersonate-btn { flex-shrink: 0; }
        .error-text { color: var(--color-error); }
      </style>
    `;

    this.renderList();

    this.querySelector('#search')!.addEventListener('input', (e) => {
      this.search = (e.target as HTMLInputElement).value;
      this.renderList();
    });

    // Navigate to edit (click on item, not on impersonate button)
    this.querySelector('#people-list')!.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.impersonate-btn')) return; // handled separately
      const item = target.closest('.list-item') as HTMLElement | null;
      if (item) router.navigate(`/admin/people/${item.dataset.id}`);
    });

    // Impersonate buttons (delegated)
    this.querySelector('#people-list')!.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('.impersonate-btn') as HTMLElement | null;
      if (!btn) return;
      e.stopPropagation();
      const personId = Number(btn.dataset.personId);
      const personName = btn.dataset.personName || '';
      auth.impersonate(personId, personName);
      toast(`Działasz jako: ${personName}`, 'info');
      router.navigate('/availability');
    });

    this.querySelector('#add-btn')!.addEventListener('click', () => {
      router.navigate('/admin/people/new');
    });
  }

  private renderList(): void {
    const filtered = this.people.filter(p => {
      if (!this.search) return true;
      const q = this.search.toLowerCase();
      return p.displayName.toLowerCase().includes(q) ||
        p.firstName.toLowerCase().includes(q) ||
        p.lastName.toLowerCase().includes(q);
    });

    this.querySelector('#people-list')!.innerHTML = filtered.map(p => `
      <div class="list-item" data-id="${p.id}" data-name="${p.displayName}">
        <div class="avatar">${p.firstName[0]}${p.lastName[0]}</div>
        <div class="list-item-content">
          <div class="list-item-title">${p.displayName}</div>
          <div class="list-item-contact">${[p.email, p.phone].filter(Boolean).join(' · ') || ''}</div>
          <div class="list-item-subtitle">${p.roles?.map(r => r.displayName).join(', ') || ''}</div>
        </div>
        ${!p.isActive ? '<span class="chip">Nieaktywny</span>' : ''}
        <button class="btn btn-secondary btn-sm impersonate-btn" data-person-id="${p.id}" data-person-name="${p.displayName}" title="Działaj jako ta osoba">👤</button>
      </div>
    `).join('');
  }
}

customElements.define('people-list-page', PeopleListPage);
